import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FilePlus,
  FileText,
  Send,
  CheckCircle,
  Clock,
  TrendingUp,
  Users,
  Briefcase,
  Sun,
  Eye,
  Filter
} from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros - Por defecto mes y año actual
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState((currentDate.getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());
  const [selectedUserName, setSelectedUserName] = useState(""); // Filtrar por nombre de usuario
  const [users, setUsers] = useState([]);

  const months = [
    { value: "all", label: "Todos" },
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
  ];

  // Generar años (últimos 5 años) + opción Todos
  const years = [
    { value: "all", label: "Todos" },
    ...Array.from({ length: 5 }, (_, i) => ({ 
      value: (currentDate.getFullYear() - i).toString(), 
      label: (currentDate.getFullYear() - i).toString() 
    }))
  ];

  const canFilterByUser = user?.role === "admin" || user?.role === "supervisor";

  useEffect(() => {
    if (canFilterByUser) {
      fetchUsers();
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear, selectedUserName]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data || []);
    } catch (error) {
      console.error("Error fetching users");
    }
  };

  const fetchData = async () => {
    try {
      // Construir params para stats
      const statsParams = new URLSearchParams();
      if (selectedMonth && selectedMonth !== "all") statsParams.append("month", selectedMonth);
      if (selectedYear && selectedYear !== "all") statsParams.append("year", selectedYear);
      if (selectedUserName) statsParams.append("user_name", selectedUserName);

      const [statsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/stats?${statsParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      
      // Filtrar cotizaciones recientes por los mismos criterios
      let filteredQuotes = quotesRes.data;
      if (selectedMonth && selectedMonth !== "all" && selectedYear && selectedYear !== "all") {
        filteredQuotes = filteredQuotes.filter(q => {
          const quoteDate = new Date(q.created_at);
          return quoteDate.getMonth() + 1 === parseInt(selectedMonth) && quoteDate.getFullYear() === parseInt(selectedYear);
        });
      } else if (selectedYear && selectedYear !== "all") {
        filteredQuotes = filteredQuotes.filter(q => {
          const quoteDate = new Date(q.created_at);
          return quoteDate.getFullYear() === parseInt(selectedYear);
        });
      }
      if (selectedUserName) {
        filteredQuotes = filteredQuotes.filter(q => q.user_name === selectedUserName);
      }
      setRecentQuotes(filteredQuotes.slice(0, 5));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: "Borrador", className: "status-draft" },
      sent: { label: "Enviada", className: "status-sent" },
      accepted: { label: "Aceptada", className: "status-accepted" },
      rejected: { label: "Rechazada", className: "status-rejected" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="dashboard">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">
          ¡Hola, {user?.name?.split(" ")[0]}!
        </h1>
        <p className="text-slate-500">
          Aquí tienes un resumen de tu actividad de cotizaciones
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filtros</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Mes:</label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[140px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600">Año:</label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[100px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canFilterByUser && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">Realizada por:</label>
              <Select value={selectedUserName || "all"} onValueChange={(v) => setSelectedUserName(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px] bg-white">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Quick Action */}
      <div className="mb-8">
        <Button
          onClick={() => navigate("/nueva-cotizacion")}
          className="action-button h-12 px-6 text-base"
          data-testid="new-quote-button"
        >
          <FilePlus className="w-5 h-5 mr-2" />
          Nueva Cotización
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="bento-grid mb-8">
        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-total"
          onClick={() => navigate("/historial")}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Cotizaciones</p>
                <p className="text-4xl font-bold text-slate-900">{stats?.total_quotes || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-draft"
          onClick={() => navigate("/historial?status=draft")}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Borradores</p>
                <p className="text-4xl font-bold text-slate-900">{stats?.draft_quotes || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-sent"
          onClick={() => navigate("/historial?status=sent")}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Enviadas</p>
                <p className="text-4xl font-bold text-sky-600">{stats?.sent_quotes || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-sky-50 flex items-center justify-center">
                <Send className="w-6 h-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-accepted"
          onClick={() => navigate("/historial?status=accepted")}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Aceptadas</p>
                <p className="text-4xl font-bold text-emerald-600">{stats?.accepted_quotes || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue & Profit by Currency */}
      <div className="mb-8">
        <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Ingresos y Utilidad por Moneda</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* MXN */}
          <Card 
            className="stats-card border-l-4 border-l-green-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/historial?currency=MXN&status=accepted")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg font-bold text-green-600">MXN</span>
                <span className="text-xs text-slate-500">Peso Mexicano</span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Ingresos</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.revenue_mxn || 0, "MXN")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Utilidad</p>
                  <p className="text-xl font-semibold text-emerald-600">{formatCurrency(stats?.profit_mxn || 0, "MXN")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* USD */}
          <Card 
            className="stats-card border-l-4 border-l-blue-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/historial?currency=USD&status=accepted")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg font-bold text-blue-600">USD</span>
                <span className="text-xs text-slate-500">Dólar Americano</span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Ingresos</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.revenue_usd || 0, "USD")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Utilidad</p>
                  <p className="text-xl font-semibold text-emerald-600">{formatCurrency(stats?.profit_usd || 0, "USD")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* EUR */}
          <Card 
            className="stats-card border-l-4 border-l-amber-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/historial?currency=EUR&status=accepted")}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg font-bold text-amber-600">EUR</span>
                <span className="text-xs text-slate-500">Euro</span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Ingresos</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.revenue_eur || 0, "EUR")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Utilidad</p>
                  <p className="text-xl font-semibold text-emerald-600">{formatCurrency(stats?.profit_eur || 0, "EUR")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Category Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-mice"
          onClick={() => navigate("/historial?category=MICE")}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">MICE</p>
                <p className="text-3xl font-bold">{stats?.mice_count || 0}</p>
                <p className="text-xs text-slate-500">Meetings, Incentives, Conferences</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="stats-card cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-leisure"
          onClick={() => navigate("/historial?category=LEISURE")}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-sky-500 flex items-center justify-center">
                <Sun className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">LEISURE</p>
                <p className="text-3xl font-bold">{stats?.leisure_count || 0}</p>
                <p className="text-xs text-slate-500">Vacaciones y Ocio</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Quotes Table */}
      <Card data-testid="recent-quotes-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl">Cotizaciones Recientes</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/historial")}
            data-testid="view-all-quotes"
          >
            Ver todas
          </Button>
        </CardHeader>
        <CardContent>
          {recentQuotes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hay cotizaciones aún</p>
              <Button
                onClick={() => navigate("/nueva-cotizacion")}
                className="mt-4"
                variant="outline"
              >
                Crear primera cotización
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header-cell">Número</TableHead>
                  <TableHead className="table-header-cell">Cliente</TableHead>
                  <TableHead className="table-header-cell">Destino</TableHead>
                  <TableHead className="table-header-cell">Tipo</TableHead>
                  <TableHead className="table-header-cell">Total</TableHead>
                  <TableHead className="table-header-cell">Estado</TableHead>
                  <TableHead className="table-header-cell">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentQuotes.map((quote) => (
                  <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                    <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                    <TableCell className="font-medium">{quote.client_name}</TableCell>
                    <TableCell>{quote.destination}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {quote.quote_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(quote.total, quote.currency)}
                    </TableCell>
                    <TableCell>{getStatusBadge(quote.status)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/cotizacion/${quote.id}`)}
                        data-testid={`view-quote-${quote.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default Dashboard;
